import { buildInitMessage, isPopupMessage } from '@cesto/web-protocol';
import type {
  InvestErrorEvent,
  InvestOptions,
  InvestHandoffEvent,
  InvestPositionClosedEvent,
  InvestScheduledEvent,
  InvestSession,
  InvestSuccessEvent,
  InvestVerifiedEvent,
} from './types';

// A closed popup usually announces itself, but not always (the user can hit
// the OS close button mid-flow), so we poll `closed` as a backstop.
//
// Two tiers, because this timer runs on the PARTNER's page for as long as the
// invest window is open — realistically minutes. The first half-minute is
// responsive so a quick dismissal fires `onClose` promptly; after that nothing
// is racing the result, so it backs off 4x rather than ticking twice a second
// for the rest of the session.
const CLOSE_POLL_FAST_MS = 500;
const CLOSE_POLL_SLOW_MS = 2_000;
const CLOSE_POLL_BACKOFF_AFTER_MS = 30_000;

/**
 * Lifecycle of one open popup: the message bridge (source + origin pinned),
 * the init handshake, once-only callback routing, and the closed poll.
 */
export class ActiveInvestSession {
  readonly session: InvestSession;

  private readonly popup: Window;
  private readonly targetOrigin: string;
  private readonly apiKey: string;
  private readonly options: InvestOptions;
  private readonly firedCallbacks = new Set<string>();
  private closePoll: ReturnType<typeof setInterval>;
  private backoffTimer: ReturnType<typeof setTimeout> | null;
  private finalized = false;

  constructor(
    popup: Window,
    targetOrigin: string,
    apiKey: string,
    options: InvestOptions,
  ) {
    this.popup = popup;
    this.targetOrigin = targetOrigin;
    this.apiKey = apiKey;
    this.options = options;

    window.addEventListener('message', this.handleMessage);
    // The popup cannot always message before closing, so poll `closed`.
    this.closePoll = setInterval(this.checkClosed, CLOSE_POLL_FAST_MS);
    this.backoffTimer = setTimeout(() => {
      this.backoffTimer = null;
      if (this.finalized) return;
      clearInterval(this.closePoll);
      this.closePoll = setInterval(this.checkClosed, CLOSE_POLL_SLOW_MS);
    }, CLOSE_POLL_BACKOFF_AFTER_MS);

    const self = this;
    this.session = {
      focus: () => this.focus(),
      close: () => {
        if (!this.popup.closed) this.popup.close();
        this.finalize();
      },
      get isOpen() {
        return self.isOpen;
      },
    };
  }

  get isOpen(): boolean {
    return !this.finalized && !this.popup.closed;
  }

  focus(): void {
    if (!this.popup.closed) this.popup.focus();
  }

  private readonly checkClosed = (): void => {
    if (this.popup.closed) this.finalize();
  };

  private readonly handleMessage = (event: MessageEvent): void => {
    // Strict pinning: only the window we opened, only the Cesto origin, only
    // well-formed protocol messages. Everything else is silently dropped.
    if (event.source !== this.popup) return;
    if (event.origin !== this.targetOrigin) return;
    if (!isPopupMessage(event.data)) return;

    const { type, payload } = event.data;
    switch (type) {
      case 'ready':
        this.sendInit();
        this.options.onReady?.();
        break;
      case 'context:verified':
        this.options.onVerified?.(payload as unknown as InvestVerifiedEvent);
        break;
      case 'invest:success':
        this.fireOnce('success', () =>
          this.options.onSuccess?.(payload as unknown as InvestSuccessEvent),
        );
        break;
      case 'invest:scheduled':
        this.fireOnce('scheduled', () =>
          this.options.onScheduled?.(
            payload as unknown as InvestScheduledEvent,
          ),
        );
        break;
      case 'invest:failed':
        this.fireOnce('error', () =>
          this.options.onError?.(payload as unknown as InvestErrorEvent),
        );
        break;
      case 'position:closed':
        // Not fireOnce: several positions can be closed in one session, and
        // each closure is reported. Unrelated to `dialog:closed` below.
        this.options.onPositionClosed?.(
          payload as unknown as InvestPositionClosedEvent,
        );
        break;
      case 'auth:handoff':
        // Dialog-mode event, forwarded here for completeness: a popup is
        // already top-level, so it signs in in place and never hands off.
        this.options.onHandoff?.(payload as unknown as InvestHandoffEvent);
        break;
      case 'dialog:closed':
        this.finalize();
        break;
      default:
        // Future informational events: ignored, never errored.
        break;
    }
  };

  private sendInit(): void {
    const { basket, amountUsd } = this.options;
    // Explicit targetOrigin, never '*': init carries the publishable key and
    // must only ever reach the Cesto origin we opened.
    this.popup.postMessage(
      buildInitMessage({
        // Wire field — named by the protocol, not by our config.
        publishableKey: this.apiKey,
        basket,
        ...(amountUsd !== undefined ? { prefill: { amountUsd } } : {}),
      }),
      this.targetOrigin,
    );
  }

  private fireOnce(key: string, fire: () => void): void {
    if (this.firedCallbacks.has(key)) return;
    this.firedCallbacks.add(key);
    fire();
  }

  private finalize(): void {
    if (this.finalized) return;
    this.finalized = true;
    window.removeEventListener('message', this.handleMessage);
    clearInterval(this.closePoll);
    if (this.backoffTimer !== null) {
      clearTimeout(this.backoffTimer);
      this.backoffTimer = null;
    }
    this.options.onClose?.();
  }
}
